/**
 * Step 4: the bulk selector. A fan taps the sports they follow, and the
 * next step (step 5) expands each tap into every league inside that sport,
 * per owner answer Q285. No sport-shaped follow is ever stored; this step
 * only stages the sport ids and advances `onboardingStep` to "leagues".
 */
import { useProfileUpdate } from "@sportsmet/profile-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import {
  NO_LEAGUES_BODY,
  NO_LEAGUES_TITLE,
} from "../scores/leagueSelectorContract";
import {
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  SPORTS_GRID_LABEL,
  SPORTS_HEADLINE,
} from "./onboardingCopy";
import { buildSportOptions, toggleSportId } from "./onboardingFollowsModel";
import { OnboardingStepFrame } from "./OnboardingStepFrame";
import { SportIcon } from "./sportIcons";

export const SportsStep = ({
  stepIndex,
  stepTotal,
  initialSportIds,
  onContinue,
}: {
  /** This fan's position and total, per Q274. Passed straight to OnboardingStepFrame. */
  stepIndex: number;
  stepTotal: number;
  /** The sports the fan already staged this session. Empty on first entry;
   *  the route keeps this across a browser Back from the leagues step. */
  initialSportIds: string[];
  /**
   * Called after /profile/update succeeds. The route advances the step and
   * raises its ceiling from `reachedServerStep`, which came straight off
   * the update response. This component never navigates.
   */
  onContinue: (input: {
    selectedSportIds: string[];
    reachedServerStep: string | null;
  }) => void;
}): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const update = useProfileUpdate();
  const [selectedSportIds, setSelectedSportIds] =
    useState<string[]>(initialSportIds);
  const isSaving = useRef(false);

  useEffect(() => {
    const code = extractErrorCode(catalog.data);
    if (code !== null)
      showApiError(catalog.data, undefined, catalog.dataUpdatedAt);
  }, [catalog.dataUpdatedAt]);

  const sports = catalog.data?.data?.sports ?? [];
  const options = buildSportOptions({ sports });

  async function handleContinue(): Promise<void> {
    if (isSaving.current) return;
    isSaving.current = true;

    let result;
    try {
      result = await update.mutateAsync({ onboardingStep: "leagues" });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(result)) {
      isSaving.current = false;
      return;
    }

    onContinue({
      selectedSportIds,
      reachedServerStep: result.data?.profile.onboardingStep ?? "leagues",
    });
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={SPORTS_HEADLINE}
      isWide
    >
      {catalog.isPending ? (
        <div
          data-testid="onboarding-sports-loading"
          className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        >
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} shape="text" />
          ))}
        </div>
      ) : options.length === 0 ? (
        <div data-testid="onboarding-sports-empty">
          <h2 className="text-body-strong text-ink">{NO_LEAGUES_TITLE}</h2>
          <p className="text-body text-ink-secondary">{NO_LEAGUES_BODY}</p>
        </div>
      ) : (
        <div
          role="group"
          aria-label={SPORTS_GRID_LABEL}
          data-testid="onboarding-sports-grid"
          className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        >
          {options.map((option) => {
            const isSelected = selectedSportIds.includes(option.sportId);
            return (
              <button
                type="button"
                key={option.sportId}
                data-testid={`onboarding-sport-card-${option.sportKey}`}
                aria-pressed={isSelected}
                onClick={() => {
                  setSelectedSportIds((current) =>
                    toggleSportId({
                      selectedSportIds: current,
                      sportId: option.sportId,
                    }),
                  );
                }}
                className={`relative flex min-h-[104px] flex-col items-center justify-center gap-1 rounded-md border-2 ${
                  isSelected ? "border-primary" : "border-hairline"
                } bg-surface p-4 text-body-strong text-ink`}
              >
                <SportIcon sportKey={option.sportKey} />
                <span>{option.name}</span>
                {isSelected ? (
                  <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-primary-solid text-on-primary">
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m5 13 4 4L19 7" />
                    </svg>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      <div className="sm-screen-bottom pt-2">
        <Button
          fullWidth
          disabled={false}
          data-testid="onboarding-sports-continue"
          onClick={() => {
            void handleContinue();
          }}
        >
          {update.isPending ? CONTINUE_PENDING_LABEL : CONTINUE_LABEL}
        </Button>
      </div>
    </OnboardingStepFrame>
  );
};
